import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

type Bullet = {
  kind: "plus" | "dash";
  text: string;
};

type Column = {
  label: string;
  labelColor: string;
  accent: string;
  title: string;
  desc: string;
  bullets: Bullet[];
  highlighted?: boolean;
};

const columns: Column[] = [
  {
    label: "JOURNALING APPS",
    labelColor: C.txt3,
    accent: C.txt3,
    title: "Secular by default.",
    desc:
      "Bullet journals, Day One, Notion templates — built for productivity or therapy, not prayer.",
    bullets: [
      { kind: "plus", text: "Great for capturing your thoughts." },
      { kind: "dash", text: "Nothing meets what you wrote." },
      { kind: "dash", text: "Scripture lives somewhere else entirely." },
    ],
  },
  {
    label: "BIBLE APPS",
    labelColor: C.sage,
    accent: C.sage,
    title: "Scripture without you in it.",
    desc:
      "YouVersion, Logos, Dwell — vast access to the text, almost no relationship to your life today.",
    bullets: [
      { kind: "plus", text: "The whole Bible in your pocket." },
      { kind: "dash", text: "Verses aren't chosen for your morning." },
      { kind: "dash", text: "No writing, no reflection, no prayer." },
    ],
  },
  {
    label: "PRAYER JOURNALS",
    labelColor: C.gold,
    accent: C.gold,
    title: "Structure without response.",
    desc:
      "Guided prayer notebooks and apps — prompts and templates, but nothing actually listens back.",
    bullets: [
      { kind: "plus", text: "Gentle daily rhythm." },
      { kind: "dash", text: "Same prompts for everyone." },
      { kind: "dash", text: "You're still writing alone." },
    ],
  },
  {
    label: "EDEN PAGES",
    labelColor: C.sage,
    accent: C.sage,
    title: "Writing, scripture, and prayer — held together.",
    desc:
      "Your words receive scripture chosen for you, and a closing prayer shaped from what you wrote.",
    bullets: [
      { kind: "plus", text: "Scripture that answers your morning." },
      { kind: "plus", text: "A closing prayer from your own words." },
      { kind: "plus", text: "One practice. Not three open tabs." },
    ],
    highlighted: true,
  },
];

export default function ComparisonSection() {
  return (
    <section
      className="py-[120px] px-6 sm:px-10"
      style={{ background: C.cream }}
    >
      <div className="max-w-[1040px] mx-auto">

        {/* Heading */}
        <FadeIn style={{ marginBottom: 72 }}>
          <div className="text-center">
            <div
              className="font-ui"
              style={{
                fontSize: 11,
                color: C.txt3,
                letterSpacing: "1.4px",
                marginBottom: 20,
              }}
            >
              WHY THIS DIDN'T EXIST YET
            </div>
            <h2
              className="font-heading font-semibold text-spec-brown mx-auto text-center"
              style={{
                fontSize: "clamp(34px, 4.2vw, 52px)",
                lineHeight: 1.1,
                maxWidth: 700,
              }}
            >
              Four ways Christians try to build a devotional life.
              <br />
              <em className="italic text-spec-sage">
                Only one holds them together.
              </em>
            </h2>
          </div>
        </FadeIn>

        {/* Comparison grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
          {columns.map((col, i) => (
            <FadeIn key={col.label} delay={i * 0.08}>
              <div
                style={
                  col.highlighted
                    ? {
                        background: C.white,
                        border: `1px solid ${C.sageBdr}`,
                        borderRadius: 4,
                        padding: "28px 24px",
                        boxShadow: "0 8px 32px rgba(107,125,86,0.10)",
                      }
                    : undefined
                }
              >
                {col.highlighted ? (
                  <span
                    className="font-ui"
                    style={{
                      display: "inline-block",
                      fontSize: 10,
                      color: C.sage,
                      letterSpacing: "1.4px",
                      background: C.sageGlow,
                      border: `1px solid ${C.sageBdr}`,
                      borderRadius: 999,
                      padding: "4px 10px",
                      marginBottom: 16,
                    }}
                  >
                    {col.label}
                  </span>
                ) : (
                  <div
                    className="font-ui"
                    style={{
                      fontSize: 11,
                      color: col.labelColor,
                      letterSpacing: "1.4px",
                      marginBottom: 14,
                    }}
                  >
                    {col.label}
                  </div>
                )}

                <h3
                  className="font-heading font-semibold"
                  style={{
                    color: C.brown,
                    fontSize: "clamp(19px, 2vw, 22px)",
                    lineHeight: 1.25,
                    marginBottom: 16,
                  }}
                >
                  {col.title}
                </h3>

                <p
                  className="font-ui"
                  style={{
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: C.txt2,
                    marginBottom: 20,
                  }}
                >
                  {col.desc}
                </p>

                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {col.bullets.map((b, j) => {
                    const isPlus = b.kind === "plus";
                    const markColor = isPlus ? col.accent : C.txt3;
                    const textColor = isPlus ? C.txt2 : C.txt3;
                    return (
                      <li
                        key={j}
                        className="font-ui"
                        style={{
                          fontSize: 13,
                          lineHeight: 1.7,
                          color: textColor,
                          display: "flex",
                          gap: 8,
                          marginBottom: j < col.bullets.length - 1 ? 6 : 0,
                        }}
                      >
                        <span
                          style={{
                            color: markColor,
                            flexShrink: 0,
                            width: 10,
                          }}
                        >
                          {isPlus ? "+" : "—"}
                        </span>
                        <span>{b.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Closing line */}
        <FadeIn delay={0.2} style={{ marginTop: 72 }}>
          <p
            className="font-heading italic mx-auto text-center"
            style={{
              fontSize: "clamp(17px, 2vw, 20px)",
              color: C.txt2,
              lineHeight: 1.8,
              maxWidth: 560,
            }}
          >
            This isn't a better journal app. It's a practice the other three
            can't hold.
          </p>
        </FadeIn>

      </div>
    </section>
  );
}
